<!--
  GeoLinkDialog.vue — the "Geo link" modal (go.lacak.io object row ⋮ → "Share location").
  Mirrors the real quick-form: a "Description (optional)" field and a "Geo link lifetime"
  select (Unlimited / 1 hour / 1 day / 1 week / 1 month / Custom, default "1 day"), with
  Cancel / Create. On Create it calls geolink/tracker/create and swaps to a "link ready"
  state showing the shareable URL + a Copy button.
  Reconstructed from the live location-link-quick-form; internal impl re-authored.
-->
<template>
  <div class="gl-backdrop" @click.self="$emit('close')">
    <div class="gl-card">
      <div class="gl-head">
        <span>Geo link</span>
        <button class="gl-x" @click="$emit('close')"><i class="material-icons">close</i></button>
      </div>

      <!-- step 1: the create form -->
      <template v-if="!created">
        <h4 class="gl-label">Description</h4>
        <input v-model="description" class="gl-input" type="text" placeholder="Description (optional)" />

        <div class="gl-field">
          <label class="gl-field-label">Geo link lifetime</label>
          <div class="gl-select" :class="{ open: pickerOpen }" @click.stop="pickerOpen = !pickerOpen">
            <span>{{ lifetime.label }}</span>
            <i class="material-icons">arrow_drop_down</i>
            <div v-if="pickerOpen" class="gl-options" @click.stop>
              <div v-for="opt in lifetimes" :key="opt.key" class="gl-option" :class="{ sel: opt.key === lifetime.key }" @click="selectLifetime(opt)">{{ opt.label }}</div>
            </div>
          </div>
        </div>

        <div v-if="lifetime.key === 'custom'" class="gl-field">
          <label class="gl-field-label">Expires at</label>
          <input v-model="customExpires" class="gl-input" type="datetime-local" :min="nowLocal" />
        </div>

        <div v-if="error" class="gl-error">{{ error }}</div>
        <div class="gl-foot">
          <div class="gl-spacer"></div>
          <button class="gl-btn" @click="$emit('close')">Cancel</button>
          <button class="gl-btn gl-primary" :disabled="busy || !canCreate" @click="create">{{ busy ? 'Creating…' : 'Create' }}</button>
        </div>
      </template>

      <!-- step 2: the created link -->
      <template v-else>
        <h4 class="gl-label">Geo link ready</h4>
        <div class="gl-linkrow">
          <input ref="linkInput" class="gl-input gl-linkinput" type="text" :value="createdUrl" readonly @focus="$event.target.select()" />
          <button class="gl-copy" :class="{ done: copied }" v-tip="'Copy link'" @click="copy"><i class="material-icons">{{ copied ? 'check' : 'content_copy' }}</i></button>
        </div>
        <div class="gl-hint">Anyone with this link can see {{ trackerLabel }}'s live location{{ expiryHint }}.</div>
        <div class="gl-foot">
          <div class="gl-spacer"></div>
          <button class="gl-btn gl-primary" @click="$emit('close')">Done</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GeoLinkDialog',
  props: {
    tracker: { type: Object, required: true }
  },
  data() {
    return {
      description: '',
      pickerOpen: false,
      customExpires: '',
      busy: false,
      error: '',
      created: false,
      createdUrl: '',
      copied: false,
      lifetimes: [
        { key: 'unlimited', label: 'Unlimited', ms: null },
        { key: 'hour', label: '1 hour', ms: 3600000 },
        { key: 'day', label: '1 day', ms: 86400000 },
        { key: 'week', label: '1 week', ms: 604800000 },
        { key: 'month', label: '1 month', ms: 2592000000 },
        { key: 'custom', label: 'Custom', ms: 0 }
      ],
      // default "1 day" (matches the real form)
      lifetime: { key: 'day', label: '1 day', ms: 86400000 }
    }
  },
  computed: {
    trackerLabel() { return this.tracker.label || ('#' + this.tracker.id) },
    expiryHint() {
      if (this.lifetime.key === 'unlimited') return ' (never expires)'
      return ''
    },
    // "Custom" requires a concrete (future) expiry before Create is allowed.
    canCreate() {
      if (this.lifetime.key !== 'custom') return true
      return !!this.customExpires && new Date(this.customExpires).getTime() > Date.now()
    },
    // local-datetime string (YYYY-MM-DDTHH:mm) for the datetime-local min attribute
    nowLocal() {
      const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      return d.toISOString().slice(0, 16)
    }
  },
  mounted() { document.addEventListener('click', this.closePicker) },
  beforeDestroy() { document.removeEventListener('click', this.closePicker); clearTimeout(this._ct) },
  methods: {
    closePicker() { this.pickerOpen = false },
    selectLifetime(opt) { this.lifetime = opt; this.pickerOpen = false },
    expiresIso() {
      if (this.lifetime.key === 'unlimited') return null
      if (this.lifetime.key === 'custom') return this.customExpires ? new Date(this.customExpires).toISOString() : null
      return new Date(Date.now() + this.lifetime.ms).toISOString()
    },
    async create() {
      this.busy = true
      this.error = ''
      try {
        const { request } = await import('@/api/request')
        const res = await request('geolink/tracker/create', {
          tracker_id: this.tracker.id,
          description: this.description || '',
          expires: this.expiresIso()
        })
        const url = res && (res.url || (res.value && res.value.url))
        if (!url) throw new Error('no url in response')
        this.createdUrl = url
        this.created = true
      } catch (e) {
        this.error = 'Could not create the geo link. Please try again.'
      }
      this.busy = false
    },
    async copy() {
      let ok = false
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) { await navigator.clipboard.writeText(this.createdUrl); ok = true }
        else if (this.$refs.linkInput) { this.$refs.linkInput.select(); ok = document.execCommand('copy') }
      } catch (e) { ok = false }
      // only show the green "copied" confirmation when the write actually succeeded
      if (!ok) { if (this.$refs.linkInput) this.$refs.linkInput.select(); return }
      this.copied = true
      clearTimeout(this._ct)
      this._ct = setTimeout(() => { this.copied = false }, 1600)
    }
  }
}
</script>

<style scoped>
.gl-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 1300; }
.gl-card { width: 320px; max-width: 92vw; background: #fff; border-radius: 6px; box-shadow: 0 8px 30px rgba(0,0,0,.32); font-family: Roboto, 'Helvetica Neue', Arial, sans-serif; padding: 16px 18px 12px; }
.gl-head { display: flex; align-items: center; justify-content: space-between; font-size: 18px; font-weight: 500; color: #263238; margin-bottom: 12px; }
.gl-x { background: none; border: none; cursor: pointer; color: #90a4ae; display: flex; padding: 0; }
.gl-x .material-icons { font-size: 20px; }
.gl-label { margin: 0 0 6px; font-size: 13px; font-weight: 500; color: #455a64; }
.gl-input { width: 100%; box-sizing: border-box; border: none; border-bottom: 1px solid #cfd8dc; padding: 6px 2px; font-size: 14px; color: #37474f; outline: none; background: none; }
.gl-input:focus { border-bottom-color: #1e88c7; }
.gl-field { margin-top: 16px; position: relative; }
.gl-field-label { font-size: 11px; color: #90a4ae; }
.gl-select { display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #cfd8dc; padding: 5px 2px; font-size: 14px; color: #37474f; cursor: pointer; position: relative; }
.gl-select .material-icons { color: #90a4ae; }
.gl-options { position: absolute; top: 30px; left: 0; right: 0; background: #fff; border-radius: 4px; box-shadow: 0 3px 14px rgba(0,0,0,.24); z-index: 10; padding: 4px 0; }
.gl-option { padding: 9px 14px; font-size: 14px; color: #37474f; }
.gl-option:hover { background: #f2f6f9; }
.gl-option.sel { color: #1e88c7; }
.gl-foot { display: flex; align-items: center; gap: 6px; margin-top: 18px; }
.gl-spacer { flex: 1; }
.gl-btn { border: none; background: none; font-size: 14px; font-weight: 500; color: #607d8b; cursor: pointer; padding: 8px 12px; border-radius: 4px; text-transform: uppercase; letter-spacing: .02em; }
.gl-btn:hover { background: #f2f6f9; }
.gl-primary { color: #1e88c7; }
.gl-primary:disabled { color: #b0bec5; cursor: default; }
.gl-linkrow { display: flex; align-items: center; gap: 6px; }
.gl-linkinput { flex: 1; color: #1e88c7; }
.gl-copy { border: none; background: none; cursor: pointer; color: #78909c; display: flex; padding: 4px; border-radius: 4px; }
.gl-copy:hover { background: #f2f6f9; color: #1e88c7; }
.gl-copy.done { color: #2e9e4b; }
.gl-copy .material-icons { font-size: 18px; }
.gl-hint { margin-top: 10px; font-size: 12px; color: #90a4ae; line-height: 1.4; }
.gl-error { margin-top: 12px; font-size: 12px; color: #e53935; }
</style>
